import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { FileText, Link as LinkIcon, File, Plus, ExternalLink, Upload, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface Resource {
  id: string;
  title: string;
  description: string;
  resource_type: string;
  resource_url: string;
  file_size?: number;
  created_at: string;
  user_id: string;
  profiles?: { first_name: string; last_name: string };
}

export function GroupResourceLibrary({ groupId }: { groupId: string }) {
  const { user } = useAuth();
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [resourceType, setResourceType] = useState<'link' | 'document'>('link');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    loadResources();
  }, [groupId]);

  const loadResources = async () => {
    setLoading(true);
    try {
      const { data: resourcesData, error } = await supabase
        .from('group_resources')
        .select('*')
        .eq('group_id', groupId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch profiles separately
      const userIds = resourcesData?.map(r => r.user_id) || [];
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', userIds);

      const profileMap = new Map(profilesData?.map(p => [p.id, p]));
      const enriched = resourcesData?.map(r => ({
        ...r,
        profiles: profileMap.get(r.user_id)
      })) || [];

      setResources(enriched);
    } catch (error) {
      console.error('Error loading resources:', error);
      toast.error('Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const addResource = async () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }

    if (resourceType === 'link' && !url.trim()) {
      toast.error('URL is required for links');
      return;
    }

    if (resourceType === 'document' && !selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      let resourceUrl = url;

      // Upload file if it's a document
      if (resourceType === 'document' && selectedFile) {
        const fileExt = selectedFile.name.split('.').pop();
        const fileName = `${groupId}/${crypto.randomUUID()}.${fileExt}`;
        
        const { error: uploadError, data } = await supabase.storage
          .from('group-resources')
          .upload(fileName, selectedFile);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('group-resources')
          .getPublicUrl(fileName);

        resourceUrl = publicUrl;
      }

      const { error } = await supabase
        .from('group_resources')
        .insert({
          group_id: groupId,
          user_id: user?.id,
          title,
          description,
          resource_type: resourceType,
          resource_url: resourceUrl,
        });

      if (error) throw error;

      // Log activity
      await supabase.from('group_activities').insert({
        group_id: groupId,
        user_id: user?.id,
        activity_type: 'resource_added',
        activity_data: { title }
      });

      toast.success('Resource added!');
      setTitle('');
      setDescription('');
      setUrl('');
      setSelectedFile(null);
      setDialogOpen(false);
      loadResources();
    } catch (error) {
      console.error('Error adding resource:', error);
      toast.error('Failed to add resource');
    } finally {
      setUploading(false);
    }
  };

  const deleteResource = async (resource: Resource) => {
    try {
      // Delete file from storage if it's a document
      if (resource.resource_type === 'document' && resource.resource_url) {
        const urlParts = resource.resource_url.split('/');
        const fileName = `${urlParts[urlParts.length - 2]}/${urlParts[urlParts.length - 1]}`;
        await supabase.storage.from('group-resources').remove([fileName]);
      }

      const { error } = await supabase
        .from('group_resources')
        .delete()
        .eq('id', resource.id);

      if (error) throw error;

      toast.success('Resource deleted');
      loadResources();
    } catch (error) {
      console.error('Error deleting resource:', error);
      toast.error('Failed to delete resource');
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'link': return <LinkIcon className="h-4 w-4" />;
      case 'document': return <FileText className="h-4 w-4" />;
      default: return <File className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-4">
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogTrigger asChild>
          <Button className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Add Resource
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Group Resource</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button
                variant={resourceType === 'link' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setResourceType('link');
                  setSelectedFile(null);
                }}
              >
                <LinkIcon className="h-4 w-4 mr-2" />
                Link
              </Button>
              <Button
                variant={resourceType === 'document' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setResourceType('document');
                  setUrl('');
                }}
              >
                <FileText className="h-4 w-4 mr-2" />
                Document
              </Button>
            </div>
            
            <Input
              placeholder="Resource title..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            
            <Textarea
              placeholder="Description (optional)..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
            
            {resourceType === 'link' ? (
              <Input
                placeholder="Resource URL..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
            ) : (
              <div className="space-y-2">
                <Input
                  type="file"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                />
                {selectedFile && (
                  <p className="text-sm text-muted-foreground">
                    Selected: {selectedFile.name}
                  </p>
                )}
              </div>
            )}
            
            <Button 
              onClick={addResource} 
              className="w-full" 
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <Upload className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Resource
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid gap-3">
        {resources.map((resource) => (
          <Card key={resource.id}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="p-2 bg-muted rounded">
                    {getIcon(resource.resource_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm">{resource.title}</h4>
                    {resource.description && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {resource.description}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                      <span>
                        Added by {resource.profiles?.first_name} {resource.profiles?.last_name}
                      </span>
                      <span>•</span>
                      <span>{format(new Date(resource.created_at), 'PP')}</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    asChild
                  >
                    <a 
                      href={resource.resource_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                  {resource.user_id === user?.id && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteResource(resource)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}